import React, { FC, useContext, useEffect, useState } from 'react';
import {
    Button,
    Checkbox,
    FormControl,
    FormControlLabel,
    Grid,
    Switch,
    TextField,
    Box,
    Alert,
    Chip,
    Divider,
    Paper,
    Typography,
} from '@mui/material';
import { LocalDentist, User } from 'models/user';
import { useMutation } from '@apollo/client';
import {
    modifyLocalDentistTemplate,
    convertLocalDentistToConnectDentistTemplate,
    setLabDentistDefaultClinicTemplate,
} from 'apollo/templates/user';
import { AppToasterContext } from 'components/shared/app-toaster/app-toaster-context';
import { useDenthubErrorBoundary } from 'components/shared/denthub-error-boundary/denthub-error-boundary-context';
import { useTranslation } from 'react-i18next';
import { useFormik } from 'formik';
import { nameRegExp } from 'config/regex';
import { LoadingButton } from '@mui/lab';
import { LinkedOrganization } from 'models/organization';
import { useNavigate } from 'react-router-dom';
import { customerSettings } from 'utils/customerSettings';
import { CONSTANTS } from 'config/constants';
import { currentOrgVar } from 'apollo/reactive-variables/organization';
import LabDefaultClinicAutocomplete from 'components/client-directory/client-settings/dentists/lab-default-clinic-autocomplete.component';

type UpdateLocalDentistFormProps = {
    onDialogClose?: () => void;
    dentistToUpdate: LocalDentist;
    onUserUpdated: (user: User) => void;
    linkedOrganizations?: LinkedOrganization[];
    isWhiteLabel?: boolean;
};
const UpdateLocalDentistForm: FC<UpdateLocalDentistFormProps> = ({
    onDialogClose,
    dentistToUpdate,
    onUserUpdated,
    linkedOrganizations,
    isWhiteLabel = false,
}: UpdateLocalDentistFormProps) => {
    const { t: translate } = useTranslation(['common', 'users', 'organization']);
    const { setAppToasterContent } = useContext(AppToasterContext);
    const { showBoundary } = useDenthubErrorBoundary();
    const [isFirstNameValid, setIsFirstNameValid] = useState<boolean>(false);
    const [isLastNameValid, setIsLastNameValid] = useState<boolean>(false);
    const navigate = useNavigate();
    // The internal dentist ID is owned by the lab, so the setting is read off the lab.
    const lab = currentOrgVar();
    const isLocalDentistIdAutoAssigned = Boolean(
        customerSettings.getCustomerSetting(
            lab?.customerSettings || [],
            CONSTANTS.SETTINGS_NAMES.AUTO_INCREMENT_LOCAL_DENTIST_ID,
        ),
    );
    const [modifyDentist, { loading }] = useMutation(modifyLocalDentistTemplate, {
        onCompleted: ({ modifyLocalDentist }) => {
            onUserUpdated(modifyLocalDentist);
        },
        onError(error) {
            showBoundary(error);
        },
    });
    const [convertLocalDentistToConnectDentist, { loading: converting }] = useMutation(
        convertLocalDentistToConnectDentistTemplate,
        {
            onCompleted: ({ convertLocalDentistToConnectDentist }) => {
                onUserUpdated(convertLocalDentistToConnectDentist);
            },
            onError(error) {
                const [firstError] = error.graphQLErrors;
                const errorType = (firstError.extensions?.exception as any)?.otherInfo?.errorType;
                if (
                    errorType === 'EMAIL_REQUIRED' ||
                    errorType === 'EMAIL_IN_USE' ||
                    errorType === 'ORG_NOT_WHITE_LABELED' ||
                    errorType === 'WL_NOT_ENABLED'
                ) {
                    setAppToasterContent({
                        isOpen: true,
                        severity: 'error',
                        message: translate(`users:${errorType}`),
                    });
                } else {
                    showBoundary(error);
                }
            },
        },
    );

    const [setLabDentistDefaultClinic, { loading: savingDefaultClinic }] = useMutation(
        setLabDentistDefaultClinicTemplate,
        {
            onError(error) {
                showBoundary(error);
            },
        },
    );

    const validate = (values: LocalDentist) => {
        const errors: Record<string, string> = {};
        if (!values.firstName) {
            errors.firstName = translate('users:VALIDATION:FIRST_NAME');
        }
        if (values.firstName && !isFirstNameValid) {
            errors.firstName = translate('common:VALIDATION.INCORRECT_NAME');
        }
        if (!values.lastName) {
            errors.lastName = translate('users:VALIDATION:LAST_NAME');
        }
        if (values.lastName && !isLastNameValid) {
            errors.lastName = translate('common:VALIDATION.INCORRECT_NAME');
        }
        if (values.convertToConnect && !values.email) {
            errors.email = translate('users:VALIDATION:EMAIL');
        }
        return errors;
    };
    const formik = useFormik({
        initialValues: {
            _id: dentistToUpdate._id,
            clinic: dentistToUpdate.clinic,
            firstName: dentistToUpdate.firstName,
            lastName: dentistToUpdate.lastName,
            email: dentistToUpdate.email,
            phone: dentistToUpdate.phone,
            note: dentistToUpdate.note,
            localId: dentistToUpdate.localId,
            active: dentistToUpdate.active,
            local: dentistToUpdate.local,
            labDefaultClinic: dentistToUpdate.labDefaultClinic,
            convertToConnect: false,
        },
        validate,
        enableReinitialize: true,
        onSubmit: async (values) => {
            const name = {
                firstName: values.firstName,
                lastName: values.lastName,
            };
            const dentist = {
                _id: values._id,
                clinic: values.clinic,
                email: values.email,
                phone: values.phone,
                note: values.note,
                localId: values.localId,
                active: values.active,
            };
            if (values.convertToConnect) {
                await convertLocalDentistToConnectDentist({ variables: { name: name, ...dentist } });
            } else {
                await modifyDentist({ variables: { name: name, ...dentist } });
            }
            if (values.labDefaultClinic !== dentistToUpdate.labDefaultClinic) {
                await setLabDentistDefaultClinic({
                    variables: {
                        dentistId: values._id,
                        mainClinic: values.clinic,
                        defaultClinic: values.labDefaultClinic || null,
                    },
                });
            }
            if (onDialogClose) {
                onDialogClose();
            }
        },
    });

    const handleFirstNameChange = (newFirstName: string) => {
        const firstName = newFirstName.charAt(0).toUpperCase() + newFirstName.slice(1);
        formik.setFieldValue('firstName', firstName);
    };

    const handleLastNameChange = (newLastName: string) => {
        const lastName = newLastName.charAt(0).toUpperCase() + newLastName.slice(1);
        formik.setFieldValue('lastName', lastName);
    };

    useEffect(() => {
        setIsFirstNameValid(nameRegExp.test(formik.values.firstName || ''));
        setIsLastNameValid(nameRegExp.test(formik.values.lastName || ''));
    }, [formik.values.firstName, formik.values.lastName]);

    const handleClinicClick = (clinicId: string) => {
        navigate(`/client-directory/clients/${clinicId}/client-details#contactDetails`);
    };

    return (
        <form onSubmit={formik.handleSubmit}>
            <Box mt={1}>
                <Paper sx={{ padding: 2 }}>
                    <Grid container spacing={3}>
                        <Grid item xs={3}>
                            <TextField
                                id="first-name"
                                label={translate('common:FIRST_NAME')}
                                variant="standard"
                                required
                                helperText={formik.touched.firstName && formik.errors.firstName}
                                error={formik.touched.firstName && Boolean(formik.errors.firstName)}
                                onChange={(e) => handleFirstNameChange(e.target.value)}
                                value={formik.getFieldProps('firstName').value}
                                name={formik.getFieldProps('firstName').name}
                                onBlur={formik.getFieldProps('firstName').onBlur}
                                inputProps={!formik.values.local ? { readOnly: true } : {}}
                                fullWidth
                            />
                        </Grid>
                        <Grid item xs={3}>
                            <TextField
                                id="last-name"
                                label={translate('common:LAST_NAME')}
                                variant="standard"
                                required
                                helperText={formik.touched.lastName && formik.errors.lastName}
                                error={formik.touched.lastName && Boolean(formik.errors.lastName)}
                                onChange={(e) => handleLastNameChange(e.target.value)}
                                value={formik.getFieldProps('lastName').value}
                                name={formik.getFieldProps('lastName').name}
                                onBlur={formik.getFieldProps('lastName').onBlur}
                                inputProps={!formik.values.local ? { readOnly: true } : {}}
                                fullWidth
                            />
                        </Grid>
                        <Grid item xs={3}>
                            <TextField
                                id="email"
                                label={translate('common:EMAIL')}
                                variant="standard"
                                type="email"
                                helperText={
                                    (formik.touched.email || formik.values.convertToConnect) && formik.errors.email
                                }
                                error={
                                    (formik.touched.email || formik.values.convertToConnect) &&
                                    Boolean(formik.errors.email)
                                }
                                inputProps={!formik.values.local ? { readOnly: true } : {}}
                                {...formik.getFieldProps('email')}
                                fullWidth
                            />
                        </Grid>
                        <Grid item xs={3}>
                            <TextField
                                id="phone"
                                label={translate('common:PHONE')}
                                variant="standard"
                                inputProps={!formik.values.local ? { readOnly: true } : {}}
                                {...formik.getFieldProps('phone')}
                                fullWidth
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                id="comments"
                                label={translate('users:INTERNAL_DENTIST_NOTE')}
                                multiline
                                minRows={4}
                                maxRows={10}
                                {...formik.getFieldProps('note')}
                                fullWidth
                            />
                            <Box mt={1}>
                                <Alert severity="info" sx={{ whiteSpace: 'pre-wrap' }}>
                                    {translate('users:DENTIST_NOTE_INFO')}
                                </Alert>
                            </Box>
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                id="local-dentist-id"
                                label={translate('users:LOCAL_DENTIST_ID')}
                                variant="standard"
                                disabled={isLocalDentistIdAutoAssigned}
                                helperText={
                                    isLocalDentistIdAutoAssigned && translate('users:LOCAL_DENTIST_ID_AUTO_ASSIGNED')
                                }
                                {...formik.getFieldProps('localId')}
                                fullWidth
                            />
                        </Grid>
                        {/* Renders its own grid cell only when this clinic is a main
                            clinic with child clinics, so it takes no space otherwise. */}
                        {formik.values.clinic && (
                            <LabDefaultClinicAutocomplete
                                mainClinicId={formik.values.clinic}
                                value={formik.values.labDefaultClinic}
                                onChange={(clinicId) => formik.setFieldValue('labDefaultClinic', clinicId ?? undefined)}
                            />
                        )}

                        <Grid item xs={4}>
                            <FormControl margin="dense" fullWidth>
                                <FormControlLabel
                                    id="active"
                                    name="active"
                                    control={
                                        <Switch
                                            color="secondary"
                                            disabled={!formik.values.local}
                                            checked={formik.values.active}
                                            onChange={formik.handleChange}
                                        />
                                    }
                                    label={
                                        formik.values.active ? translate('common:ACTIVE') : translate('common:INACTIVE')
                                    }
                                />
                            </FormControl>
                        </Grid>
                        {isWhiteLabel && formik.values.local && (
                            <Grid item xs={4}>
                                <FormControl margin="dense" fullWidth>
                                    <FormControlLabel
                                        id="convertToConnect"
                                        name="convertToConnect"
                                        control={
                                            <Checkbox
                                                color="secondary"
                                                checked={formik.values.convertToConnect}
                                                onChange={formik.handleChange}
                                            />
                                        }
                                        label={translate('users:CONVERT_TO_CONNECTED_DENTIST')}
                                    />
                                </FormControl>
                            </Grid>
                        )}
                    </Grid>
                </Paper>
            </Box>
            <Box mb={2} mt={2}>
                <Divider />
            </Box>
            {linkedOrganizations && linkedOrganizations.length > 0 && (
                <Box mb={2}>
                    <Typography variant="subtitle2" gutterBottom>
                        {translate('organization:RELATED_CLINICS')}
                    </Typography>
                    <Box display="flex" flexWrap="wrap" gap={1}>
                        {linkedOrganizations.map((org) => (
                            <Chip
                                key={org._id}
                                label={org.name}
                                variant="outlined"
                                clickable
                                color="primary"
                                onClick={() => handleClinicClick(org._id)}
                                sx={{
                                    cursor: 'pointer',
                                }}
                            />
                        ))}
                    </Box>
                </Box>
            )}

            <Box display="flex" justifyContent="flex-end">
                {onDialogClose && <Button onClick={onDialogClose}>{translate('common:CANCEL')}</Button>}
                <LoadingButton
                    disabled={!formik.isValid || !dentistToUpdate.clinic}
                    variant="contained"
                    type="submit"
                    loading={loading || converting || savingDefaultClinic}
                >
                    {translate('common:SAVE')}
                </LoadingButton>
            </Box>
        </form>
    );
};

export default UpdateLocalDentistForm;
